import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  IconButton,
  Input,
  Td,
  Textarea,
  Tr,
  useDisclosure,
} from '@chakra-ui/react';
import { observer } from 'mobx-react-lite';
import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';

import { useStore } from '../../hooks/useStore';
import CustomTable from '../../components/table/CustomTable';
import DetailDrawer from '../../components/drawer/DetailDrawer';
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import { speedMessageHeaders } from '../../constants/tableHeaders';
import { parseDate } from '../../utils/date';
import useDebounce from '../../hooks/useDebounce';
import SearchBar from '../../components/common/SearchBar';

const SpeedMessagesScreen = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    speedMessageStore: {
      speedMessages,
      totalSpeedMessages,
      loading,
      fetchSpeedMessages,
      createSpeedMessage,
      updateSpeedMessage,
      deleteSpeedMessage,
      speedMessagesSearch,
      setSpeedMessagesSearch,
    },
  } = useStore();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);

  const debouncedSearch = useDebounce(speedMessagesSearch, 500);

  useEffect(() => {
    setCurrentPage(1);
    (async () => await fetchSpeedMessages({ page: 1, limit: 10 }))();
  }, [debouncedSearch]);

  const openCreate = () => {
    setEditingId(null);
    setText('');
    onOpen();
  };

  const openEdit = (id: string, currText: string) => {
    setEditingId(id);
    setText(currText);
    onOpen();
  };

  const onSave = async () => {
    if (!text.trim()) return;
    if (editingId) {
      await updateSpeedMessage(editingId, text.trim());
    } else {
      await createSpeedMessage(text.trim());
    }
    onClose();
  };

  const askDelete = (id: string) => {
    setDeleteId(id);
    setIsDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (deleteId) {
      await deleteSpeedMessage(deleteId);
    }
    setIsDeleteOpen(false);
    setDeleteId(null);
  };

  return (
    <Box>
      <SearchBar
        search={speedMessagesSearch}
        setSearch={setSpeedMessagesSearch}
        placeholder={'Search for speed message'}
      />
      <HStack justify="flex-end" mt={4} mb={4}>
        <Button leftIcon={<FaPlus />} colorScheme="blue" onClick={openCreate}>
          Add Message
        </Button>
      </HStack>
      <CustomTable
        headers={speedMessageHeaders}
        rowsPerPage={10}
        totalRows={totalSpeedMessages}
        fetchDataTable={fetchSpeedMessages}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        loading={loading}
      >
        {(speedMessages || []).map((msg) => (
          <Tr key={msg.id}>
            <Td maxW="800px">{msg.id}</Td>
            <Td maxW="800px">{msg.text}</Td>
            <Td maxW="800px">{parseDate(msg.createdAt.toString()).date}</Td>
            <Td maxW="800px">{parseDate(msg.updatedAt.toString()).date}</Td>
            <Td whiteSpace="nowrap">
              <HStack spacing={3}>
                <IconButton
                  aria-label="Edit"
                  icon={<FaEdit />}
                  size="sm"
                  onClick={() => openEdit(msg.id, msg.text)}
                />
                <IconButton
                  aria-label="Delete"
                  icon={<FaTrash />}
                  size="sm"
                  colorScheme="red"
                  variant="outline"
                  onClick={() => askDelete(msg.id)}
                />
              </HStack>
            </Td>
          </Tr>
        ))}
      </CustomTable>

      <DetailDrawer isOpen={isOpen} onClose={onClose}>
        <Box display="grid" gap={4}>
          <Textarea
            placeholder="Enter message text…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={5}
          />
          <HStack justify="flex-end" spacing={3}>
            <Button onClick={onClose} variant="outline">
              Cancel
            </Button>
            <Button
              onClick={onSave}
              colorScheme="blue"
              isDisabled={!text.trim()}
            >
              Save
            </Button>
          </HStack>
        </Box>
      </DetailDrawer>

      <ConfirmationModal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        title="Delete message?"
        subtitle="This action cannot be undone."
        cancelActionLabel="Cancel"
        confirmActionLabel="Delete"
        onCancel={() => setIsDeleteOpen(false)}
        onConfirm={confirmDelete}
      />
    </Box>
  );
};

export default observer(SpeedMessagesScreen);
